import { t } from "@travily/i18n";
import familyIllustration from "@ui/assets/illustrations/family-animated.webp";
import { Wizard } from "@ui/components/Wizard";
import { WizardTextArea } from "@ui/components/WizardTextArea";
import type { ReactNode } from "react";

const COLOR = "red" as const;

export interface FamilyBioStepViewProps {
  /** Wizard progress: 1-based index of the current stage. */
  currentStage: number;
  /** Wizard progress: total number of stages. */
  totalStages: number;
  /** Navigate to the previous stage. Omit to hide the back button. */
  onBack?: () => void;
  /** Top-bar tertiary actions (e.g. "Finish later"). */
  topBarActions: ReactNode;
  /** Advance to / submit the current stage. Drives both the Skip and Continue buttons. */
  onPrimary: () => void;
  /** Whether the primary button shows a pending spinner. */
  primaryPending: boolean;
  /** Current value of the bio field. */
  value: string;
  /** Field error for the bio input, or undefined when valid. */
  error?: string;
  /** Called with the new value on every field change. */
  onChange: (value: string) => void;
  /** Called when the field loses focus (drives blur-time validation). */
  onBlur?: () => void;
}

export function FamilyBioStepView({
  currentStage,
  totalStages,
  onBack,
  topBarActions,
  onPrimary,
  primaryPending,
  value,
  error,
  onChange,
  onBlur,
}: FamilyBioStepViewProps) {
  return (
    <Wizard color={COLOR} currentStage={currentStage} totalStages={totalStages} onBack={onBack}>
      {topBarActions}
      <Wizard.SecondaryButton onPress={onPrimary} mobileLabel={t("common.actions.skip")}>
        {t("common.actions.skip")}
      </Wizard.SecondaryButton>
      <Wizard.PrimaryButton onPress={onPrimary} isPending={primaryPending}>
        {t("common.actions.continue")}
      </Wizard.PrimaryButton>
      <Wizard.Stage
        illustration={<img alt="" aria-hidden="true" src={familyIllustration} />}
        heading={t("web.families.creator.steps.bio.show.title")}
        helper={t("web.families.creator.steps.bio.show.subtitle")}
      >
        <div className="w-full max-w-[600px]">
          <WizardTextArea
            autoFocus
            color={COLOR}
            errorMessage={error}
            isInvalid={Boolean(error)}
            isLabelHidden
            label={t("web.accounts.setup.steps.bio.label")}
            onBlur={onBlur}
            onChange={onChange}
            placeholder={t("web.families.creator.steps.bio.show.placeholder")}
            rows={4}
            value={value}
          />
        </div>
      </Wizard.Stage>
    </Wizard>
  );
}
